import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { apiError, EmploymentStatus, type CreateEmployeeInput, type Employee } from './lib';
import EmployeeDetail from './EmployeeDetail';

const BLANK: CreateEmployeeInput = { employeeCode: '', firstName: '', lastName: '' };

/**
 * The staff directory. Never shows a wage — see `EmployeeDetail`'s own
 * comment and `compensation-rbac.spec.ts` on the backend for the negative
 * test this screen must not undo by rendering a field it happened to
 * receive. `GET /employees` never carries compensation in the first place
 * (`EMPLOYEE_SELECT` is an allow-list), so there is nothing to accidentally
 * leak here even before the permission gate on the detail panel.
 */
export default function EmployeesTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canCreate = can('employees.create');

  const [rows, setRows] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<EmploymentStatus | ''>(EmploymentStatus.ACTIVE);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: Employee[] }>('/employees', {
        params: { limit: 100, query: search || undefined, status: status || undefined },
      })
      .then(({ data }) => {
        const list = data.data ?? [];
        setRows(list);
        setSelectedId((current) => (current && list.some((e) => e.id === current) ? current : (list[0]?.id ?? null)));
      })
      .catch((e) => setError(apiError(e, t('workforce.employees.loadFailed'))))
      .finally(() => setLoading(false));
  }, [search, status, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post<Employee>('/employees', {
        ...form,
        lastName: form.lastName || undefined,
      });
      toast.success(t('workforce.employees.created'));
      setFormOpen(false);
      setForm(BLANK);
      setSelectedId(data.id);
      load();
    } catch (e) {
      toast.error(apiError(e, t('workforce.employees.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[22rem_1fr]">
      <Card className="h-fit">
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle>{t('workforce.employees.title')}</CardTitle>
          {canCreate && (
            <Button size="sm" onClick={() => setFormOpen(true)}>
              {t('common.add')}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder={t('workforce.employees.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select value={status} onChange={(e) => setStatus(e.target.value as EmploymentStatus | '')}>
            <option value="">{t('workforce.employees.anyStatus')}</option>
            {Object.values(EmploymentStatus).map((s) => (
              <option key={s} value={s}>
                {t(`workforce.employmentStatus.${s}`)}
              </option>
            ))}
          </Select>

          {loading ? (
            <LoadingState label={t('common.loading')} />
          ) : error ? (
            <ErrorState message={error} onRetry={load} />
          ) : rows.length === 0 ? (
            <EmptyState title={t('workforce.employees.empty')} hint={t('workforce.employees.emptyHint')} />
          ) : (
            <ul className="scrollbar-thin max-h-[32rem] divide-y overflow-y-auto rounded-lg border">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    onClick={() => setSelectedId(row.id)}
                    className={`w-full p-3 text-start text-sm hover:bg-hover ${row.id === selectedId ? 'bg-accent-soft' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">
                        {row.firstName} {row.lastName ?? ''}
                      </span>
                      <Badge tone={toneForStatus(row.status)}>
                        {t(`workforce.employmentStatus.${row.status}`)}
                      </Badge>
                    </div>
                    <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-ink-muted">
                      <span>{row.employeeCode}</span>
                      <span>{row.jobTitle ?? '—'}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div>
        {selectedId ? (
          <EmployeeDetail employeeId={selectedId} onChanged={load} />
        ) : (
          <Card>
            <CardContent className="p-5">
              <EmptyState title={t('workforce.employees.nothingSelected')} />
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t('workforce.employees.newEmployee')}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => void submit()} disabled={saving || !form.employeeCode.trim() || !form.firstName.trim()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('workforce.employees.employeeCode')} required>
            <Input value={form.employeeCode} onChange={(e) => setForm({ ...form, employeeCode: e.target.value })} />
          </Field>
          <Field label={t('workforce.employees.firstName')} required>
            <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          </Field>
          <Field label={t('workforce.employees.lastName')}>
            <Input value={form.lastName ?? ''} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </Field>
          <Field label={t('workforce.employees.jobTitle')}>
            <Input value={form.jobTitle ?? ''} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
